import { useEffect, useState } from 'react';
import { useInView } from '../hooks/useInView';
import { techStack } from '../data/portfolioData';

const ROW2 = techStack;

// const enterAnimations = ['slideLeft'];
// const exitAnimations = ['slideLeftOut', 'slideUpOut'];
const exitAnimations=['slideToLeftOut','slideToDownOut','slideToRightOut','slideToUpOut']
const enterAnimations=['slideToDownIn','slideToLeftIn','slideToUpIn','slideToRightIn']

function PausableMarqueeRow({ items }) {
  const [visibleCount, setVisibleCount] = useState(7);
  const [slots, setSlots] = useState([]);

  // RESPONSIVE ITEM COUNT
  useEffect(() => {
    const updateCount = () => {
      const width = window.innerWidth;
      if (width < 480) setVisibleCount(2);
      else if (width < 640) setVisibleCount(3);
      else if (width < 768) setVisibleCount(4);
      else if (width < 1024) setVisibleCount(5);
      else setVisibleCount(7);
    };
    updateCount();
    window.addEventListener('resize', updateCount);
    return () => window.removeEventListener('resize', updateCount);
  }, []);

  // INITIAL ITEMS
  useEffect(() => {
    const initialSlots = [];
    for (let i = 0; i < visibleCount; i++) {
      initialSlots.push({
        current: items[i % items.length],
        exiting: null,
      });
    }
    setSlots(initialSlots);
  }, [items, visibleCount]);

  // STAGGERED ROTATE LOGIC
  useEffect(() => {
    if (!items.length || visibleCount === 0) return;

    let slotIndex = 0;
    let itemBaseIndex = visibleCount;
    let timeoutId;
    let isCancelled = false;

    function tick() {
      if (isCancelled) return;

      const newItemIndex = (itemBaseIndex + slotIndex) % items.length;
      const newItem = items[newItemIndex];
      const currentSlotIdx = slotIndex;

      // STEP 1: Start Exit Animation Immediately & clear 'current'
      setSlots((prev) => {
        const next = [...prev];
        if (next[currentSlotIdx] && next[currentSlotIdx].current !== newItem) {
          next[currentSlotIdx] = {
            current: null, // Temporarily empty so it doesn't crossfade
            exiting: next[currentSlotIdx].current, 
          };
        }
        return next;
      });

      // STEP 2: Introduce the new entering item after a delay (e.g., 350ms)
      // (Adjust 350ms based on how long you want the slot to stay empty)
      setTimeout(() => {
        if (isCancelled) return;
        setSlots((prev) => {
          const next = [...prev];
          if (next[currentSlotIdx]) {
            next[currentSlotIdx] = {
              ...next[currentSlotIdx],
              current: newItem,
            };
          }
          return next;
        });
      }, 600); 

      // STEP 3: Cleanup the exiting item once its CSS animation finishes (700ms total)
      setTimeout(() => {
        if (isCancelled) return;
        setSlots((prev) => {
          const next = [...prev];
          if (next[currentSlotIdx]) {
            next[currentSlotIdx] = {
              ...next[currentSlotIdx],
              exiting: null
            };
          }
          return next;
        });
      }, 700);

      slotIndex++;

      if (slotIndex < visibleCount) {
        // Stagger between each slot's transition sequence
        timeoutId = setTimeout(tick, 200);
      } else {
        // Pause delay after the whole group has swapped
        slotIndex = 0;
        itemBaseIndex = (itemBaseIndex + visibleCount) % items.length;
        timeoutId = setTimeout(tick, 2500);
      }
    }

    // Start the loop after an initial delay
    timeoutId = setTimeout(tick, 1500);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [items, visibleCount]);

  return (
    <>
      <style>{`
        .skills-rotator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.2rem;
          overflow: hidden;
          flex-wrap: nowrap;
          width: 100%;
          mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
        }
        .skill-slot {
          position: relative;
          flex: 0 0 auto;
          width: clamp(88px, 10vw, 110px);
          height: clamp(88px, 10vw, 110px);
          overflow: hidden;
        }
        .rotating-skill-card {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          border-radius: 14px;
          border: 1px solid var(--border);
          background: var(--card);
          transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
          animation-duration: 0.7s;
          animation-fill-mode: both;
          animation-timing-function: ease;
        }
        .rotating-skill-card.exiting { pointer-events: none; }
        .rotating-skill-card:hover { transform: translateY(-5px); }
        .rotating-skill-name { font-size: clamp(0.65rem, 1vw, 0.74rem); color: var(--text2); white-space: nowrap; }
        
        @keyframes slideLeft { from { opacity: 0; transform: translateX(-50%); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideLeftOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-100%); } }
        @keyframes slideUpOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-100%); } }
        
        @keyframes slideToLeftIn { from { opacity: 0; transform: translateX(50%); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideToDownIn { from { opacity: 0; transform: translateY(-50%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideToUpIn { from { opacity: 0; transform: translateY(50%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideToRightIn { from { opacity: 0; transform: translateX(-50%); } to { opacity: 1; transform: translateX(0); } }

        @keyframes slideToLeftOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(-100%); } }
        @keyframes slideToDownOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(100%); } }
        @keyframes slideToUpOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-100%); } }
        @keyframes slideToRightOut { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(100%); } }

        
      `}</style>

      <div className="skills-rotator">
        {slots.map((slot, index) => {
          // CRITICAL FIX: Removed "!slot.current" from this check so exiting cards 
          // still render even when "current" is null during the sequence gap.
          if (!slot) return null;

          const enterAnim = enterAnimations[index % enterAnimations.length];
          const exitAnim = exitAnimations[index % exitAnimations.length];

          return (
            <div className="skill-slot" key={`slot-${index}`}>
              {/* Exiting Item (Fading Out) */}
              {slot.exiting && (
                <div
                  key={`exit-${slot.exiting.name}`}
                  className="rotating-skill-card exiting"
                  style={{ animationName: exitAnim }}
                >
                  <slot.exiting.icon
                    size={window.innerWidth < 640 ? 28 : slot.exiting.size}
                    color={slot.exiting.color}
                  />
                  <span className="rotating-skill-name">{slot.exiting.name}</span>
                </div>
              )}

              {/* Entering Item (Fading In) - Conditionally rendered only when active */}
              {slot.current && (
                <div
                  key={`enter-${slot.current.name}`}
                  className="rotating-skill-card entering"
                  style={{ animationName: enterAnim }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = slot.current.color + '88';
                    e.currentTarget.style.background = slot.current.color + '14';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${slot.current.color}22`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'var(--card)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <slot.current.icon
                    size={window.innerWidth < 640 ? 28 : slot.current.size}
                    color={slot.current.color}
                  />
                  <span className="rotating-skill-name">{slot.current.name}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function Skills() {
  const [ref, inView] = useInView(0.1);

  return (
    <section id="skills" style={{ background: 'var(--bg2)' }} ref={ref}>
      <div className="max-w-5xl mx-auto px-6 lg:px-16 pt-24 pb-0">
        <div className={`reveal ${inView ? 'visible' : ''}`}>
          <div className="section-eyebrow">02 — What I work with</div>
        </div>
        <h2
          className={`reveal ${inView ? 'visible' : ''} delay-1`}
          style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}
        >
          Skills & Tools
        </h2>
        <p
          className={`reveal ${inView ? 'visible' : ''} delay-2`}
          style={{ color: 'var(--text2)', fontSize: '1.05rem', maxWidth: 520, lineHeight: 1.7, marginBottom: '3.5rem' }}
        >
          Technologies I've worked with across frontend, backend, and automation.
        </p>
      </div>

      <div
        className={`reveal ${inView ? 'visible' : ''} delay-3`}
        style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', paddingBottom: '5rem' }}
      >
        <PausableMarqueeRow items={ROW2} />
      </div>
    </section>
  );
}